/**
 * Оболочка приложения: боковое меню слева, шапка сверху, содержимое справа.
 *
 * Меню разное для разных ролей — воркеру не нужны «Штрафы» и «Хранилище».
 * Показываем не то, что человек не сможет открыть, а только то, чем он
 * реально пользуется.
 *
 * На телефоне меню занимало бы треть экрана, поэтому там оно выезжает
 * поверх содержимого по кнопке в шапке и закрывается само при переходе.
 */
import { createContext, useContext, useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../lib/auth';
import { api } from '../lib/api';
import { Avatar } from './ui';
import type { Video } from '../lib/types';

type NavItem = { to: string; icon: string; label: string; badge?: number };

/**
 * Открыть меню может только шапка, а живёт оно в оболочке.
 * Контекст — чтобы не тащить обработчик через все страницы пропсами.
 */
const MenuContext = createContext<() => void>(() => {});

export function Layout() {
  const { user, can, hasRole, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  // Переход по ссылке закрывает меню: иначе на телефоне после нажатия
  // остаёшься смотреть на то же меню, а страница сменилась под ним
  useEffect(() => setMenuOpen(false), [location.pathname]);

  const isReviewer = can('video.review');

  // Счётчики в меню обновляем нечасто: они справочные, а лишние запросы
  // при 150 видео в месяц ни к чему
  const { data: reviewQueue } = useQuery({
    queryKey: ['review-queue-count'],
    queryFn: () => api.get<Video[]>('/api/videos/review'),
    enabled: isReviewer,
    refetchInterval: 60_000,
  });

  const { data: myQueue } = useQuery({
    queryKey: ['my-queue-count'],
    queryFn: () => api.get<Video[]>('/api/videos/queue'),
    enabled: hasRole('worker') || hasRole('editor'),
    refetchInterval: 60_000,
  });

  const items: NavItem[] = isReviewer
    ? [
        { to: '/', icon: '◈', label: 'Дашборд' },
        { to: '/review', icon: '◉', label: 'Приёмка', badge: reviewQueue?.length },
        { to: '/tasks', icon: '▤', label: 'Все ТЗ' },
        { to: '/finance', icon: '₽', label: 'Финансы' },
      ]
    : [
        { to: '/', icon: '◈', label: 'Главная' },
        { to: '/tasks', icon: '▤', label: 'Мои задачи', badge: myQueue?.length },
        { to: '/finance', icon: '₽', label: 'Финансы' },
      ];

  const secondary: NavItem[] = can('settings.manage')
    ? [
        { to: '/team', icon: '◍', label: 'Команда' },
        { to: '/templates', icon: '⧉', label: 'Шаблоны' },
        { to: '/clients', icon: '◫', label: 'Заказчики' },
        { to: '/penalties', icon: '⚠', label: 'Штрафы' },
        { to: '/storage', icon: '▦', label: 'Хранилище' },
        { to: '/settings', icon: '⚙', label: 'Настройки' },
      ]
    : [{ to: '/profile', icon: '◍', label: 'Профиль' }];

  return (
    <div className="flex h-full">
      {/* Затемнение под выехавшим меню — и способ закрыть его тычком мимо */}
      {menuOpen && (
        <div
          className="fixed inset-0 bg-black/60 z-30 md:hidden"
          onClick={() => setMenuOpen(false)}
        />
      )}

      <aside
        className={`w-[236px] shrink-0 bg-side border-r border-line flex flex-col p-3 pt-[18px]
          fixed inset-y-0 left-0 z-40 transition-transform duration-200 overflow-y-auto
          md:static md:z-auto md:translate-x-0 md:transition-none
          ${menuOpen ? 'translate-x-0' : '-translate-x-full'}`}
      >
        <div className="px-2.5 pb-[22px] text-[21px] tracking-[0.01em] font-extrabold text-accent2">
          Конторка
        </div>

        <nav>
          {items.map((it) => (
            <Item key={it.to} {...it} />
          ))}
          <div className="h-px bg-line my-3 mx-2.5" />
          {secondary.map((it) => (
            <Item key={it.to} {...it} />
          ))}
        </nav>

        <div className="mt-auto pt-3 border-t border-line">
          <button
            onClick={() => navigate('/profile')}
            className="w-full flex items-center gap-2.5 p-2 rounded-lg hover:bg-white/[0.04] transition-colors text-left"
          >
            {user && <Avatar id={user.id} name={user.displayName} url={user.avatarUrl} size={30} />}
            <div className="min-w-0 flex-1">
              <div className="text-[13px] font-semibold truncate">{user?.displayName}</div>
              <div className="text-[10.5px] text-muted2 truncate">{roleLabel(user?.roles ?? [])}</div>
            </div>
          </button>
          <button
            onClick={() => void logout().then(() => navigate('/'))}
            className="w-full mt-1 px-2 py-1.5 text-[11.5px] text-muted2 hover:text-dang transition-colors text-left"
          >
            Выйти
          </button>
        </div>
      </aside>

      <main className="flex-1 flex flex-col min-w-0">
        {/* key заставляет содержимое проигрывать появление при каждом переходе —
            это дешёвый способ сделать навигацию ощутимо живой */}
        <div key={location.pathname} className="flex-1 overflow-y-auto animate-fade">
          <MenuContext.Provider value={() => setMenuOpen(true)}>
            <Outlet />
          </MenuContext.Provider>
        </div>
      </main>
    </div>
  );
}

function Item({ to, icon, label, badge }: NavItem) {
  return (
    <NavLink
      to={to}
      end={to === '/'}
      className={({ isActive }) =>
        `flex items-center gap-3 px-[11px] py-[9px] rounded-[9px] mb-0.5 text-[13.5px] font-semibold transition-colors ${
          isActive ? 'bg-accent/12 text-accent2' : 'text-muted hover:bg-white/[0.04] hover:text-ink'
        }`
      }
    >
      <span className="w-[17px] text-center text-sm opacity-85">{icon}</span>
      {label}
      {badge ? (
        <span className="ml-auto bg-accent text-white text-[10.5px] font-bold px-1.5 rounded-md min-w-[19px] text-center">
          {badge}
        </span>
      ) : null}
    </NavLink>
  );
}

/** Заголовок страницы. Вынесен сюда, чтобы отступы и размеры были одинаковыми везде. */
export function PageHeader({
  title,
  sub,
  back,
  actions,
}: {
  title: string;
  sub?: string;
  back?: string;
  actions?: React.ReactNode;
}) {
  const navigate = useNavigate();
  const openMenu = useContext(MenuContext);

  return (
    <div
      className="min-h-[60px] py-2.5 md:h-[60px] md:py-0 shrink-0 border-b border-line
        flex items-center flex-wrap md:flex-nowrap px-4 md:px-7 gap-x-3.5 gap-y-2
        sticky top-0 bg-bg z-20"
    >
      {/* Кнопка меню — только на телефоне, на большом экране меню и так видно */}
      <button
        onClick={openMenu}
        className="md:hidden text-muted hover:text-ink text-xl leading-none px-1 -ml-1 transition-colors"
        aria-label="Меню"
      >
        ≡
      </button>

      {back && (
        <button
          onClick={() => navigate(back)}
          className="text-muted hover:text-ink text-xl px-1 transition-colors"
        >
          ‹
        </button>
      )}
      <div className="min-w-0">
        <h1 className="text-[17px] md:text-[19px] font-bold tracking-[-0.02em] truncate">
          {title}
        </h1>
        {sub && <div className="text-xs text-muted2 truncate">{sub}</div>}
      </div>
      <div className="flex-1" />
      {/* На узком экране кнопки переносятся на вторую строку, а не сжимаются в кашу */}
      {actions && <div className="w-full md:w-auto">{actions}</div>}
    </div>
  );
}

export function Page({ children }: { children: React.ReactNode }) {
  return <div className="px-4 py-4 md:px-7 md:py-6 pb-16 max-w-[1180px]">{children}</div>;
}

function roleLabel(roles: string[]): string {
  const names: Record<string, string> = {
    admin: 'Администратор',
    manager: 'Менеджер',
    worker: 'Съёмщик',
    editor: 'Монтажер',
  };
  return roles.map((r) => names[r] ?? r).join(' · ') || 'Без роли';
}
